import { useCallback, useEffect, useState } from 'react';

import debounce from '@/utils/debounce';
import { createLogger, LOG_NAMESPACES } from '@/utils/logger';

const logger = createLogger(LOG_NAMESPACES.CACHE);

// Ajusta la ruta según tu estructura de carpetas

/**
 * Custom hook to manage state synchronized with localStorage.
 *
 * @template T - The type of the value to be stored.
 * @param {string} key - The key under which the value is stored in localStorage.
 * @param {T} initialValue - The initial value to be used if there is no value in localStorage.
 * @param {number} [delay=300] - The delay in milliseconds for debouncing updates to localStorage.
 * @returns {[T, (value: T | ((val: T) => T)) => void]} - Returns a stateful value and a function to update it.
 *
 * @example
 * const [name, setName] = useLocalStorage<string>('name', 'John Doe');
 */
function useLocalStorage<T>(key: string, initialValue: T, delay: number = 300): [T, (value: T | ((val: T) => T)) => void] {
  // Local state to store the value
  const [storedValue, setStoredValue] = useState<T>(() => {
    if (typeof window === 'undefined') {
      return initialValue;
    }

    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      logger.error(`Error getting value from localStorage for key '${key}':`, error);
      return initialValue;
    }
  });

  // Update the value in localStorage and in the state
  const setValue = useCallback(
    debounce((value: T | ((val: T) => T)) => {
      if (typeof window === 'undefined') {
        return;
      }

      try {
        const currentValue = window.localStorage.getItem(key);
        const parsedCurrentValue = currentValue ? JSON.parse(currentValue) : initialValue;
        const valueToStore = value instanceof Function ? value(parsedCurrentValue) : value;

        if (valueToStore === parsedCurrentValue) return;

        setStoredValue(valueToStore);
        window.localStorage.setItem(key, JSON.stringify(valueToStore));
      } catch {
        // Silently ignore errors
      }
    }, delay),
    [],
  );

  // Load the value from localStorage when the key changes
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === key) {
        try {
          const newValue = event.newValue ? JSON.parse(event.newValue) : initialValue;
          setStoredValue(newValue);
        } catch (error) {
          logger.error(`Error parsing new value from localStorage for key '${key}':`, error);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [key, initialValue]);

  return [storedValue, setValue] as const;
}

export default useLocalStorage;
